/** Student user id from a teacher/students relationship row (never use row.id — that is the relationship PK). */
export function studentPkFromRelationship(s) {
  if (!s || s.student == null) return null;
  if (typeof s.student === 'number') return s.student;
  if (typeof s.student === 'string') {
    const n = parseInt(s.student, 10);
    return Number.isNaN(n) ? null : n;
  }
  if (typeof s.student === 'object' && s.student.id != null) return Number(s.student.id);
  if (s.student_id != null) return Number(s.student_id);
  return null;
}

export function normalizeTeacherStudents(list) {
  const raw = (list || [])
    .filter((s) => {
      const rel = String(s.relationship_type || '').toUpperCase();
      if (rel === 'PARENT') return false;
      if (rel && rel !== 'TEACHER') return false;
      if (String(s.is_accepted || '').toLowerCase() !== 'accepted') return false;
      return studentPkFromRelationship(s) != null;
    })
    .map((s) => {
      const id = studentPkFromRelationship(s);
      const name = (s.student_name || '').trim();
      return {
        ...s,
        id,
        relationshipId: s.id,
        email: (s.student_email || '').trim(),
        label: name || s.student_email || `Student #${id}`,
      };
    });
  const byId = new Map();
  raw.forEach((row) => {
    if (row.id != null && !byId.has(row.id)) byId.set(row.id, row);
  });
  return Array.from(byId.values());
}

export function isAttemptCompleted(att) {
  if (!att) return false;
  const st = String(att.status || '').toUpperCase();
  return !!(att.submitted || att.end_time || ['SUBMITTED', 'GRADED', 'TIMED_OUT'].includes(st));
}

export function bestAttemptsByTest(attempts) {
  const map = new Map();
  for (const att of attempts || []) {
    if (!isAttemptCompleted(att)) continue;
    const raw = att.test ?? att.test_id;
    const tid = typeof raw === 'object' && raw != null ? Number(raw.id) : Number(raw);
    if (tid == null || Number.isNaN(tid)) continue;
    const prev = map.get(tid);
    const tCur = att.end_time ? new Date(att.end_time).getTime() : 0;
    const tPrev = prev?.end_time ? new Date(prev.end_time).getTime() : 0;
    if (!prev || tCur >= tPrev) map.set(tid, att);
  }
  return map;
}

export function computeStudentSnapshot(assignments, attempts) {
  const byTest = bestAttemptsByTest(attempts);
  const scores = [];
  let timely = 0;
  let timelyDenom = 0;
  for (const row of Array.isArray(assignments) ? assignments : []) {
    const raw = row.test ?? row.test_id;
    const testId = typeof raw === 'object' && raw != null ? Number(raw.id) : Number(raw);
    if (testId == null || Number.isNaN(testId)) continue;
    const att = byTest.get(testId);
    if (att && isAttemptCompleted(att)) {
      const sc = att.score != null && att.score !== '' ? Number(att.score) : null;
      if (sc != null && !Number.isNaN(sc)) scores.push(sc);
    }
    const td = row.target_date;
    if (td && att && isAttemptCompleted(att) && att.end_time) {
      timelyDenom += 1;
      const endDay = String(att.end_time).slice(0, 10);
      const targetDay = String(td).slice(0, 10);
      if (endDay <= targetDay) timely += 1;
    }
  }
  const avgScore = scores.length
    ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
    : null;
  const timelyPct = timelyDenom > 0 ? Math.round((100 * timely) / timelyDenom) : null;
  return { avgScore, timelyPct };
}

export async function fetchAllTeacherStudentRelationships(api, basePath) {
  const all = [];
  let page = 1;
  const pageSize = 100;
  for (;;) {
    const res = await api.get(basePath, { params: { page, page_size: pageSize, is_active: true } });
    const body = res.data;
    const chunk = Array.isArray(body) ? body : body?.results || [];
    all.push(...chunk);
    if (chunk.length < pageSize) break;
    if (typeof body?.count === 'number' && all.length >= body.count) break;
    page += 1;
    if (page > 100) break;
  }
  return all;
}

export function aggregateBatchStats(members, statsByStudentId) {
  const n = (members || []).length;
  if (!n) {
    return { studentCount: 0, batchAvgScore: null, batchTimelyPct: null, statsLoading: false };
  }
  let scoreSum = 0;
  let scoreN = 0;
  let timSum = 0;
  let timN = 0;
  let statsLoading = false;
  for (const m of members) {
    const st = statsByStudentId[m.student];
    if (!st || st.loading) {
      statsLoading = true;
      continue;
    }
    if (st.avgScore != null) {
      scoreSum += st.avgScore;
      scoreN += 1;
    }
    if (st.timelyPct != null) {
      timSum += st.timelyPct;
      timN += 1;
    }
  }
  return {
    studentCount: n,
    batchAvgScore: scoreN ? Math.round((scoreSum / scoreN) * 10) / 10 : null,
    batchTimelyPct: timN ? Math.round((timSum / timN) * 10) / 10 : null,
    statsLoading,
  };
}

export function displayName(m) {
  const n = `${m.first_name || ''} ${m.last_name || ''}`.trim();
  return n || '—';
}

export function initialsFromMember(m) {
  const f = (m.first_name || '').trim().charAt(0);
  const l = (m.last_name || '').trim().charAt(0);
  if (f && l) return `${f}${l}`.toUpperCase();
  const em = (m.email || '').trim();
  if (em.length >= 2) return em.slice(0, 2).toUpperCase();
  return '?';
}
